// @givmo/board — one record in the minute book, and everything recorded about it.
//
// ══ WHAT THIS SCREEN IS RESPONSIBLE FOR GETTING RIGHT ═══════════════════════════════════════════
//
//   * THE TWO LINEAGES NEVER SHARE A HEADING. A correction says an earlier record was WRONG; a version
//     says the board ADOPTED A REPLACEMENT. They are different acts with different consequences (a
//     correction chain deletes as a unit; a version chain does not), and a CHECK refuses both on one
//     row. They render under separate headings, in separate words, always.
//
//   * A POINTER THAT DID NOT ARRIVE IS NOT RENDERED. Every record-to-record pointer is scoped like the
//     record it names, so one the reader may not follow is simply absent from the projection — and it
//     leaves this page as nothing at all. No "restricted" chip, no greyed row, no count. The reader is
//     not entitled to learn that the withheld thing exists, and even the correction event NOTES on
//     either side carry no id for the same reason.
//
//   * NOTHING JOINS `participants` CLIENT-SIDE. The participant read is user-keyed with no seat
//     column, so a directorless-account participant simply is not a row — absence already encodes
//     participation on every read path. Rendering a "who was present" list from it would turn a
//     deliberate key-space split into a false roster of the room.
//
//   * THE APPROVAL EVIDENCE IS WHAT WAS RECORDED, NOT WHAT IT ACCOMPLISHED. The approval line is plan
//     §3.4's "As approved by [person] on [date]", composed in ONE place (`recordApprovalLine`) from the
//     recorded name, the kind of approver and the instant. This page adds no sentence about effect,
//     and there is deliberately no field, badge or phrasing that would read as one.
//
//   * A REFUSAL IS SHOWN IN THE BACKEND'S OWN WORDS. The deletion refusals are written for exactly
//     this moment — the chain one names the chain size and points at the operation that can act on
//     it, the retention one is arithmetic over the record's own dates — and the api-client's own
//     guidance is to render `message` verbatim and branch on `status`, never on text. So this page
//     branches on 409 plus what the record itself shows, and prints the sentence.
//
//   * AN IMPORTED RECORD SAYS HOW IT CAME IN (CBG-UX11, design-284 §2.3; frames r1, r2): the
//     organization's note when it gave one, then who entered it by hand and when, or who a connected
//     AI proposed it for, through which client, and who imported it (CBG-AI5-PROPOSE, copy 7.2) — all
//     from the record's own read, never a batch. A record with a file Board holds opens it here (copy
//     5.4): `RecordLineageLine`, `RecordFile`.
//
//   * THE APPROVAL ECHOES THE HASH THE READ SERVED, CAPTURED AT DIALOG-OPEN. `pending_content_hash`
//     is derived on the read by the authoritative implementation; this app computes nothing. See
//     {@link ApproveRecordDialog} for why the capture point is the semantic.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '../../board-routing';
import {
  isGivmoError,
  type BoardRecordEventOut,
  type OrgBoardRecordReadOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Icon,
  Skeleton,
  TextField,
  Textarea,
  saasButtonVariants,
  toast,
} from '@givmo/design-system';
import { Lock } from 'lucide-react';
import { Fingerprint } from '../../components/fingerprint';
import { PageShell } from '../../components/page-shell';
import { StatusWord } from '../../components/fine-print';
import { FieldNote } from '../../components/field-note';
import { ReadOnlyDate } from '../../components/read-only-date';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { DeleteRecordDialog } from './delete-record-dialog';
import { BOARD_PAGES } from '../../board-routes';
import {
  approvalSignatureMarkLine,
  confidentialityBadge,
  displayDate,
  eventActorLine,
  filedConsentLog,
  makingEventWords,
  eventCaptureLine,
  eventInstant,
  eventTransition,
  eventNote,
  eventTitle,
  extractRecordEvents,
  isCalendarDate,
  isImported,
  isTheRecord,
  meetingMinutesOf,
  pointersOfLineage,
  provenanceLine,
  recordApprovalBody,
  recordApprovalEcho,
  recordApprovalLine,
  recordDate,
  recordDateWording,
  recordKindLabelOf,
  recordPointers,
  recordStandingLabel,
} from './records-decode';
import { useBoardOperations, useBoardVocab } from '../../board-runtime';
// CBG-AI5-PROPOSE: the proposal a record came from, and the file Board holds for it.
import { AiRationale } from '../proposals/ai-rationale';
import { RecordFile, RecordLineageLine } from '../proposals/record-lineage';
import { importWords } from '../import/import-words';

/** A destructive act's text button: red text, no solid fill (the danger menu item's colours). */
const DISCARD_CLASSES =
  'text-danger-600 hover:bg-danger-50 hover:text-danger-700 focus-visible:shadow-ring-danger';

export function RecordDetailPage() {
  const operations = useBoardOperations();
  const { orgNoun } = useBoardVocab();
  const { recordId } = useParams({ from: '/authenticated/records/$recordId' });
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canApprove = hasBoardPermission(permissions, 'board_record:approve');
  const canDelete = hasBoardPermission(permissions, 'board_record:delete');
  // Editing a draft's wording, sending it for approval and discarding it are the draft door —
  // `board_record:write`, which neither of the two above implies.
  const canWriteDraft = hasBoardPermission(permissions, 'board_record:write');

  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  // ⚠ THE APPROVAL ECHO IS CAPTURED INTO STATE AT DIALOG-OPEN, NOT READ FROM THE LIVE QUERY.
  //
  // `recordQuery.data` changes under an open dialog for reasons the officer never sees —
  // `refetchOnReconnect` is on by default, `staleTime` is 30s, and a sibling mutation invalidates the
  // key. Reading the echo when the confirm button fires means a mid-dialog refetch swaps BOTH the
  // content behind the dialog AND the hash that goes out: the approval matches the NEW bytes, no 409
  // fires, and the officer has approved a version they never read. Capturing at open turns that into
  // the DESIGNED refusal. The same capture point, for the same reason, as the minutes surface. The
  // record's DATE is captured with it (CBG-FIX6): whether the dialog shows the date or asks for one
  // is decided by the read the approver saw, not by a refetch.
  const [approving, setApproving] = useState<{
    contentHash: string;
    occurredOn: string | null;
    recordKind: string;
  } | null>(null);

  const recordKey = ['board-record', orgRef, recordId] as const;
  const eventsKey = ['board-record-events', orgRef, recordId] as const;

  const recordQuery = useQuery({
    queryKey: recordKey,
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordReadOut> => {
      const { data } = await operations.officerGetBoardRecord({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        throwOnError: true,
      });
      return data;
    },
  });

  const eventsQuery = useQuery({
    queryKey: eventsKey,
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<BoardRecordEventOut[]> => {
      const { data } = await operations.officerListBoardRecordEvents({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        throwOnError: true,
      });
      return extractRecordEvents(data);
    },
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: recordKey });
    await queryClient.invalidateQueries({ queryKey: eventsKey });
    await queryClient.invalidateQueries({ queryKey: ['board-records', orgRef] });
    await queryClient.invalidateQueries({
      queryKey: ['board-governing-documents', orgRef],
    });
  };

  const record = recordQuery.data ?? null;

  const submit = useMutation({
    mutationFn: async () => {
      await operations.officerSubmitBoardRecord({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        body: {},
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Sent for approval.');
      await refresh();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t do that. Please try again.'),
  });

  const discard = useMutation({
    mutationFn: async () => {
      await operations.officerDiscardBoardRecordDraft({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        body: {},
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Draft discarded.');
      await refresh();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t do that. Please try again.'),
  });

  if (recordQuery.isPending) {
    return (
      <PageShell title="Record" testId="page-record-detail">
        <Skeleton className="h-90 w-full rounded-lg" testId="record-detail-loading" />
      </PageShell>
    );
  }

  if (recordQuery.isError || record === null) {
    return (
      <PageShell title="Record" testId="page-record-detail">
        <Card testId="record-detail-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t open this record.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void recordQuery.refetch()}
              testId="record-detail-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const pointers = recordPointers(record);
  const corrections = pointersOfLineage(pointers, 'correction');
  const versions = pointersOfLineage(pointers, 'version');
  const links = pointersOfLineage(pointers, 'link');
  const closed = confidentialityBadge(record);
  const provenance = provenanceLine(record);
  const events = eventsQuery.data ?? [];
  const theRecord = isTheRecord(record);
  // A yearly form's line reads its approval event's capture, so it waits for the events (D13): no
  // line while they load, and the record's own served sentence if they can't be read (the same
  // approval event's words, without the time). Never the import line below, which is an import's.
  const servedStatement = (record.approval_statement ?? '').trim();
  const approvalLine =
    recordApprovalLine(record, eventsQuery.isSuccess ? events : undefined) ??
    (eventsQuery.isError && servedStatement.length > 0 ? servedStatement : null);
  const consentLog = filedConsentLog(record);
  // ⚠ Taken from THE OBJECT THIS PAGE RENDERED FROM, and handed to the dialog as a prop. Nothing here
  // computes a hash and nothing re-reads to obtain one. See `records-decode.ts` → `recordApprovalEcho`.
  const echo = recordApprovalEcho(record);
  // `submitted` content stays editable, and that is SAFE precisely because approval echoes the
  // content hash: an edit after review makes the reviewer's echo stale and the approval 409s. There
  // is deliberately no "return to draft" transition to reach instead.
  const contentEditable = record.state === 'draft' || record.state === 'submitted';
  // CBG-FIX8 — a MEETING's minutes (the meeting's own, or a closed session's) are edited and approved
  // on that meeting's Minutes page: the record core's editor, submit and approval refuse them. So this
  // page offers none of those for them — it points at the meeting instead — and keeps only the discard,
  // which is how an officer starts a meeting's minutes over.
  const meetingMinutes = meetingMinutesOf(record);

  return (
    <PageShell
      title={record.title}
      description={
        // The status as a plain word, as the Minute book row says it (CBG-UX1's rule: colour only
        // for a status someone must act on — "Awaiting approval").
        <>
          {recordKindLabelOf(record)}
          {record.occurred_on ? ` · ${displayDate(record.occurred_on)}` : ''}
          {' · '}
          <StatusWord
            tone={record.state === 'submitted' ? 'act' : 'plain'}
            testId="record-detail-state"
          >
            {/* True per kind (CBG-UX3): a disclosure is "Recorded", a filed consent "Signed by
                everyone" (CBG-UX-COPY2's A6). */}
            {recordStandingLabel(record)}
          </StatusWord>
          {/* An imported record carries ONE "Imported" (D-CBG-9): its state reads it, so the
              provenance word renders only where the state would not already say so. */}
          {isImported(record) && record.state !== 'imported' ? (
            <>
              {' · '}
              <StatusWord testId="record-detail-imported">Imported</StatusWord>
            </>
          ) : null}
          {closed !== null ? (
            <>
              {' · '}
              <span
                className="inline-flex items-center gap-4 whitespace-nowrap"
                data-testid="record-detail-confidentiality"
              >
                <Icon icon={Lock} size={12} aria-hidden />
                {closed.label}
              </span>
            </>
          ) : null}
        </>
      }
      actions={
        <Link
          to="/records"
          // Exact: this page sits beneath that address, and it is not the current page.
          exact
          className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          data-testid="record-detail-back"
        >
          Minute book
        </Link>
      }
      testId="page-record-detail"
    >
      {/* ── What it says ───────────────────────────────────────────────────────────────────── */}
      {editing && contentEditable && meetingMinutes === null ? (
        <DraftEditor
          orgRef={orgRef ?? ''}
          recordId={recordId}
          title={record.title}
          body={record.body}
          occurredOn={record.occurred_on ?? ''}
          recordKind={record.record_kind}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await refresh();
          }}
        />
      ) : (
        <Card testId="record-detail-text">
          <CardBody className="flex flex-col gap-12">
            <div className="flex flex-wrap items-start justify-between gap-12">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                What it says
              </h2>
              {contentEditable && canWriteDraft && meetingMinutes === null ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => setEditing(true)}
                  testId="record-detail-edit"
                >
                  Edit the wording
                </Button>
              ) : null}
            </div>
            {/* A card is never a bare heading: an empty record says so, quietly. */}
            {(record.body ?? '').trim().length > 0 ? (
              <pre
                className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="record-detail-body"
              >
                {record.body}
              </pre>
            ) : (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="record-detail-body-empty"
              >
                {/* An imported record never takes text later (CBG-UX11, design-284 §2.3). */}
                {isImported(record)
                  ? record.imported_artifact_file_name
                    ? importWords.textInTheFile
                    : importWords.noTextRecorded
                  : 'No text yet.'}
              </p>
            )}
            {record.artifact_ref ? (
              <span
                className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                data-testid="record-detail-artifact"
              >
                Your copy: <code className="select-all font-mono">{record.artifact_ref}</code>
              </span>
            ) : null}
            {record.imported_artifact_file_name ? (
              <RecordFile orgRef={orgRef ?? ''} record={record} />
            ) : null}
          </CardBody>
        </Card>
      )}

      {/* ── Where it came from: a person's note when it has one, then how it came in ─────────── */}
      {isImported(record) ? (
        <Card testId="record-detail-provenance">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Where this came from
            </span>
            {record.import_origin === 'connected_ai' ? (
              <>
                {/* CBG-UX12: an AI's proposal says how it came in first, the line that names "an AI
                    assistant", and the note the AI wrote waits after it, behind its one link. */}
                <RecordLineageLine record={record} />
                {provenance !== null ? (
                  <AiRationale note={provenance} testId="record-detail-source-note" />
                ) : null}
              </>
            ) : (
              <>
                {provenance !== null ? (
                  <span
                    className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary wrap-anywhere"
                    data-testid="record-detail-source-note"
                  >
                    {provenance}
                  </span>
                ) : null}
                <RecordLineageLine record={record} />
              </>
            )}
          </CardBody>
        </Card>
      ) : null}

      {/* ── How it became the record ───────────────────────────────────────────────────────── */}
      {theRecord ? (
        <Card testId="record-detail-approval">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              How this became your record
            </span>
            {approvalLine !== null ? (
              // Plan §3.4's line: who approved and when, and nothing about what that approval
              // accomplished. A director's approval says "as a board member" (§3.3).
              <span
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="record-detail-approval-statement"
              >
                {approvalLine}
              </span>
            ) : isImported(record) ? (
              <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                Imported as it stood — nobody approved it here, because your board already did that
                at the time.
              </span>
            ) : null}
            {consentLog.length > 0 ? (
              // A filed consent's own signature log, from the record itself (CBG-UX3): the act was
              // every director's signature, so the page shows them — the same rows the readable copy
              // prints, each seat named as it stood when the consent was filed.
              <div className="flex flex-col gap-4 pt-4" data-testid="record-detail-consent-log">
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  Its signatures, in the order they were recorded
                </span>
                <ul className="flex flex-col gap-6">
                  {consentLog.map((act) => (
                    <li
                      key={act.key}
                      className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                      data-testid={`record-detail-consent-act-${act.key}`}
                    >
                      {act.day} · {act.name} · {act.words}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {record.content_hash ? (
              // The WHOLE fingerprint, selectable and copyable, as on the machine-readable copy's
              // dialog: a shortened one cannot be compared against anything.
              <div className="flex flex-col gap-6 pt-4">
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  Its fingerprint is
                </span>
                <Fingerprint value={record.content_hash} testId="record-detail-hash" />
                <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  Change a word and the fingerprint changes.
                </span>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* ── Corrections ────────────────────────────────────────────────────────────────────── */}
      {corrections.length > 0 ? (
        <Card testId="record-detail-corrections">
          <CardBody className="flex flex-col gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Corrections
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              A correction doesn’t replace what was recorded — both stay, and they are kept
              together.
            </p>
            {corrections.map((pointer) => (
              <PointerRow
                key={pointer.field}
                field={pointer.field}
                label={pointer.label}
                recordId={pointer.recordId}
              />
            ))}
          </CardBody>
        </Card>
      ) : null}

      {/* ── Version lineage. A DIFFERENT act, under its own heading, in its own words. ──────── */}
      {versions.length > 0 ? (
        <Card testId="record-detail-versions">
          <CardBody className="flex flex-col gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Where this sits in the document’s history
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              Your board adopted this in place of an earlier version. That is a different thing from
              a correction: the earlier version wasn’t wrong, it was replaced.
            </p>
            {versions.map((pointer) => (
              <PointerRow
                key={pointer.field}
                field={pointer.field}
                label={pointer.label}
                recordId={pointer.recordId}
              />
            ))}
            <Link
              to={BOARD_PAGES.governance}
              className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="record-detail-registry-link"
            >
              All your governing documents
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* ── What it points at ──────────────────────────────────────────────────────────────── */}
      {links.length > 0 ? (
        <Card testId="record-detail-links">
          <CardBody className="flex flex-col gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              What this is connected to
            </h2>
            {links.map((pointer) => (
              <PointerRow
                key={pointer.field}
                field={pointer.field}
                label={pointer.label}
                recordId={pointer.recordId}
              />
            ))}
          </CardBody>
        </Card>
      ) : null}

      {/* ── The event log ──────────────────────────────────────────────────────────────────── */}
      <Card testId="record-detail-events">
        <CardBody className="flex flex-col gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            Everything that has happened to it
          </h2>
          {eventsQuery.isPending ? (
            <Skeleton className="h-48 w-full rounded-lg" testId="record-detail-events-loading" />
          ) : null}
          {/* Every record's creation writes an event, so an empty log is rare (a legacy row) — and
              a card is never a bare heading. */}
          {eventsQuery.isSuccess && events.length === 0 ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-tertiary"
              data-testid="record-detail-events-empty"
            >
              Nothing has happened to it yet.
            </p>
          ) : null}
          <ul className="flex flex-col gap-12">
            {events.map((event) => {
              // The event that made a disclosure or a filed consent the record is worded by what
              // it was (CBG-UX3); every other event keeps its words.
              const making = makingEventWords(record, event);
              const actor = making !== null ? making.actor : eventActorLine(event, record);
              const capture = eventCaptureLine(event);
              const mark = approvalSignatureMarkLine(event);
              const transition = making !== null ? making.detail : eventTransition(event);
              // CBG-FIX69: a participant's note names an account by its id; the line names them.
              const note = eventNote(event);
              return (
                <li
                  key={event.id}
                  className="flex flex-col gap-2 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
                  data-testid={`record-event-${event.id}`}
                >
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {making !== null ? making.title : eventTitle(event, orgNoun)}
                  </span>
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    {eventInstant(event)}
                    {transition !== null ? ` · ${transition}` : ''}
                  </span>
                  {actor !== null ? (
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {actor}
                    </span>
                  ) : null}
                  {capture !== null ? (
                    <span
                      className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                      data-testid={`record-event-capture-${event.id}`}
                    >
                      {capture}
                    </span>
                  ) : null}
                  {mark !== null ? (
                    <span
                      className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                      data-testid={`record-event-mark-${event.id}`}
                    >
                      {mark}
                    </span>
                  ) : null}
                  {note !== null ? (
                    <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {note}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </CardBody>
      </Card>

      {error !== null ? (
        <ErrorMessage testId="record-detail-action-error">{error}</ErrorMessage>
      ) : null}

      {/* ── What can still be done ─────────────────────────────────────────────────────────── */}
      {/* Offered only to someone who can take one of its acts: the draft door (send, discard) is
          `board_record:write`, approving is `board_record:approve`. A reader sees the state badge. */}
      {meetingMinutes !== null && contentEditable ? (
        <Card testId="record-detail-meeting-minutes">
          <CardBody className="flex flex-col items-start gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              These are a meeting’s minutes
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              They’re written and approved on the meeting’s Minutes page, where they stay matched to
              what was recorded at the meeting.
            </p>
            <div className="flex flex-wrap items-center gap-10">
              <Link
                to="/meetings/$meetingId/minutes"
                params={{ meetingId: meetingMinutes.meetingId }}
                {...(meetingMinutes.segmentId !== null
                  ? { search: { session: meetingMinutes.segmentId } }
                  : {})}
                className={saasButtonVariants({
                  size: 'md',
                  tone: 'secondary',
                  className: 'no-underline',
                })}
                data-testid="record-detail-meeting-minutes-link"
              >
                Open the meeting’s minutes
              </Link>
              {canWriteDraft ? (
                <Button
                  appearance="saas"
                  tone="ghost"
                  // Destructive, marked as the meeting's "Cancel meeting" is: red text, no solid
                  // red button (the design system's danger menu item: danger-600 on danger-50).
                  className={DISCARD_CLASSES}
                  loading={discard.isPending}
                  onClick={() => {
                    setError(null);
                    discard.mutate();
                  }}
                  testId="record-detail-discard"
                >
                  Discard this draft
                </Button>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : (record.state === 'draft' && canWriteDraft) ||
        (record.state === 'submitted' && (canWriteDraft || canApprove)) ? (
        <Card testId="record-detail-lifecycle">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {record.state === 'draft' ? 'When you’re ready' : 'Waiting on an officer'}
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              A draft is not the record. It becomes one when an officer approves it, and that
              approval names the exact wording above — change a word and the approval has to be
              given again.
            </p>
            {record.state === 'submitted' && echo === null && canApprove ? (
              // The read serves no address to approve against. It is not a state this surface can
              // explain away, so it names it and withdraws the button rather than sending an empty
              // echo the backend would refuse with a message about the record having changed.
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="record-detail-no-echo"
              >
                We can’t read this record’s fingerprint just now, so approving it here would name
                nothing. Reload the page, and if it stays this way it needs someone to look at it.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-10">
              {record.state === 'draft' && canWriteDraft ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={submit.isPending}
                  onClick={() => {
                    setError(null);
                    submit.mutate();
                  }}
                  testId="record-detail-submit"
                >
                  Send it for approval
                </Button>
              ) : null}
              {record.state === 'submitted' && echo !== null && canApprove ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  onClick={() => {
                    setError(null);
                    // Captured HERE, from the read that rendered the text above — not when the
                    // confirm button fires.
                    setApproving({
                      contentHash: echo,
                      occurredOn: recordDate(record),
                      recordKind: record.record_kind,
                    });
                  }}
                  testId="record-detail-approve"
                >
                  Approve it
                </Button>
              ) : null}
              {canWriteDraft ? (
                <Button
                  appearance="saas"
                  tone="ghost"
                  // Destructive, marked as the meeting's "Cancel meeting" is: red text, no solid
                  // red button (the design system's danger menu item: danger-600 on danger-50).
                  className={DISCARD_CLASSES}
                  loading={discard.isPending}
                  onClick={() => {
                    setError(null);
                    discard.mutate();
                  }}
                  testId="record-detail-discard"
                >
                  Discard this draft
                </Button>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {theRecord ? (
        <Card testId="record-detail-deletion">
          <CardBody className="flex flex-col items-start gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Removing this from your minute book
            </h2>
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
              data-testid="record-detail-retention-note"
            >
              {/* The retention and hold facts are every reader's; how a removal works is said only
                  to someone who can ask for one (`board_record:delete`). */}
              {canDelete
                ? 'These records are yours, and you can have one removed. It is never silent: the reason you give is kept, and the fact of the removal outlives the record itself. Records are held for at least ten years from the date they are about, and a legal hold keeps anything it covers.'
                : 'These records are yours. Records are held for at least ten years from the date they are about, and a legal hold keeps anything it covers.'}
            </p>
            {canDelete ? (
              <Button
                appearance="saas"
                tone="danger"
                onClick={() => setDeleting(true)}
                testId="record-detail-delete"
              >
                Remove this record
              </Button>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {approving !== null && orgRef !== null ? (
        <ApproveRecordDialog
          orgRef={orgRef}
          recordId={recordId}
          contentHash={approving.contentHash}
          occurredOn={approving.occurredOn}
          recordKind={approving.recordKind}
          onClose={() => setApproving(null)}
          onApproved={async () => {
            setApproving(null);
            await refresh();
          }}
        />
      ) : null}

      {deleting && orgRef !== null ? (
        <DeleteRecordDialog orgRef={orgRef} record={record} onClose={() => setDeleting(false)} />
      ) : null}
    </PageShell>
  );
}

/**
 * Approving a submitted record, echoing the address the read served.
 *
 * ══ WHY THE ECHO ARRIVES AS A PROP, AND WHY THIS APP NO LONGER COMPUTES ONE ═══════════════════
 *
 * `POST …/records/{id}/approval` takes a REQUIRED `content_hash`, and the approver's is the hash of
 * the bytes they read — a mismatch is refused, which is what stops content changing under an approver
 * between submission and approval. This app used to PORT the backend's canonical hashing form to
 * produce it (11 sorted keys, compact separators, a version string inside the hashed bytes), because
 * a draft published no hash to read. That port was a second copy of frozen backend law in a second
 * language, held only by golden vectors, and it could not answer at all for a record carrying a
 * structured `payload` — Python and JavaScript disagree about an integral float, undetectably.
 *
 * The backend now derives `pending_content_hash` on every read of a record that is not yet the
 * record, from the same `record.compute_hash()` the seal runs. So the authoritative implementation
 * computes it, on the read that rendered the content, for EVERY draft — including the
 * payload-carrying ones the port refused. The port is gone, and with it the refusal surface that
 * existed only because of its limitation.
 *
 * What remains, and is the whole point: the echo comes from the SAME READ the approver was shown,
 * captured at dialog-open by the caller. This component neither reads the record nor computes
 * anything, so there is no path by which it could name bytes nobody looked at.
 *
 * ══ THE DATE (CBG-FIX6; D-CBG-8) ═══════════════════════════════════════════════════════════════
 *
 * A record is approved only with the date it is about. When the record has one, the dialog shows it
 * — the approval seals it, so the approver should see it — and sends nothing more. When it has none,
 * the dialog asks for it and the approval carries it: the backend checks the echo against the stored
 * bytes first, then records the date as this approver's edit and seals the dated content, all in one
 * act. So a date typed here can never launder an edit someone else made after the approver read the
 * page. A date the record already carries is never changed here — that is an edit of the draft.
 *
 * The date is named for the kind of record (`recordDateWording`): "Date adopted" on a
 * governing-document version, "Decision date" on a resolution, "Meeting date" on minutes, "Date"
 * otherwise.
 */
function ApproveRecordDialog({
  orgRef,
  recordId,
  contentHash,
  occurredOn,
  recordKind,
  onClose,
  onApproved,
}: {
  orgRef: string;
  recordId: string;
  /** ⚠ The address from the read that rendered what the approver read. Never re-fetched here. */
  contentHash: string;
  /** The record's date from that same read, or `null` when it has none and this dialog asks for it. */
  occurredOn: string | null;
  /** The record's kind from that same read — it names the date. */
  recordKind: string;
  onClose: () => void;
  onApproved: () => void | Promise<void>;
}) {
  const operations = useBoardOperations();
  const [error, setError] = useState<string | null>(null);
  const needsDate = occurredOn === null;
  const dateWording = recordDateWording(recordKind);
  const [suppliedDate, setSuppliedDate] = useState('');
  const dateReady = !needsDate || isCalendarDate(suppliedDate);

  const approve = useMutation({
    mutationFn: async () => {
      // A typed const, not a literal at the call: the date is the one member the committed snapshot
      // does not declare yet (see `recordApprovalBody`).
      const body = recordApprovalBody(contentHash, needsDate ? suppliedDate : null);
      await operations.officerApproveBoardRecord({
        path: { org_ref: orgRef, record_id: recordId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Approved. It is part of your minute book from now on.');
      await onApproved();
    },
    onError: (caught) =>
      // The backend's own sentence. A 409 here says the wording moved since it was read, and the
      // action that answers it is to read the current version — which a generic retry line would
      // send the officer round in a loop instead of saying.
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      ),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !approve.isPending && onClose()}>
      <Dialog.Content
        aria-label="Approve this record"
        className="flex flex-col gap-16"
        testId="record-approval-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Approve this record?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          This records that you, as the officer signed in, approved it today. From then it is part
          of your minute book and its wording and date are fixed — a later change is recorded as a
          correction.
        </p>

        {needsDate ? (
          <>
            <TextField
              label={dateWording.label}
              type="date"
              value={suppliedDate}
              onChange={(event) => setSuppliedDate(event.target.value)}
              testId="record-approval-date-input"
            />
            <FieldNote testId="record-approval-date-note">{dateWording.hint}</FieldNote>
          </>
        ) : (
          <ReadOnlyDate label={dateWording.label} date={occurredOn} testId="record-approval-date" />
        )}

        <FieldNote testId="record-approval-echo">
          Approving names the exact wording shown on this page. If it has changed since you opened
          it, this will be refused and you can read the current version.
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="record-approval-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={approve.isPending}
            onClick={onClose}
            testId="record-approval-cancel"
          >
            Not yet
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={approve.isPending}
            disabled={!dateReady}
            onClick={() => {
              setError(null);
              approve.mutate();
            }}
            testId="record-approval-confirm"
          >
            Approve it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * One pointer row.
 *
 * Rendered ONLY for a pointer that arrived — `recordPointers` omits the rest, and there is
 * deliberately no variant of this component for an absent one.
 */
function PointerRow({
  field,
  label,
  recordId,
}: {
  field: string;
  label: string;
  recordId: string;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid={`record-pointer-${field}`}>
      <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {label}
      </span>
      <Link
        to="/records/$recordId"
        params={{ recordId }}
        className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
        data-testid={`record-pointer-link-${field}`}
      >
        Open that record
      </Link>
    </div>
  );
}

/**
 * Editing a draft's text in place.
 *
 * Reachable while the record is a `draft` OR `submitted`, which mirrors the backend's
 * `CONTENT_MUTABLE_STATES` rather than being more cautious than it. Keeping `submitted` editable is
 * safe *because* approval echoes the content hash: an edit after review makes the reviewer's echo
 * stale and the approval refuses, which is exactly the signal that wants raising. There is
 * deliberately no "return to draft" transition, so this is the only way a submitted record's wording
 * changes.
 *
 * Nothing here touches the descriptor of a governing-document version — its instrument, label and
 * lineage are set at creation and the backend does not accept them on this route. A wrong descriptor
 * is fixed the way every other mistake in a record is: discard the draft, or correct the record.
 */
function DraftEditor({
  orgRef,
  recordId,
  title: initialTitle,
  body: initialBody,
  occurredOn: initialOccurredOn,
  recordKind,
  onCancel,
  onSaved,
}: {
  orgRef: string;
  recordId: string;
  title: string;
  body: string;
  occurredOn: string;
  /** Names the date field (`recordDateWording`). */
  recordKind: string;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const operations = useBoardOperations();
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [occurredOn, setOccurredOn] = useState(initialOccurredOn);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerUpdateBoardRecordDraft({
        path: { org_ref: orgRef, record_id: recordId },
        body: {
          title: title.trim(),
          body,
          // Omitted rather than blanked when the organization recorded no governance date.
          ...(occurredOn.length > 0 ? { occurred_on: occurredOn } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Draft saved.');
      await onSaved();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.'),
  });

  return (
    <Card testId="record-detail-editor">
      <CardBody className="flex flex-col gap-12">
        <TextField
          label="Title"
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          testId="record-edit-title"
        />
        <TextField
          label={recordDateWording(recordKind).label}
          type="date"
          value={occurredOn}
          onChange={(event) => setOccurredOn(event.target.value)}
          testId="record-edit-occurred-on"
        />
        <Textarea
          label="What it says"
          rows={14}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          testId="record-edit-body"
        />

        <FieldNote>
          Changing the wording after it has gone for approval means the approval has to be given
          against the new wording — the old one no longer matches.
        </FieldNote>

        {error !== null ? <ErrorMessage testId="record-edit-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onCancel}
            testId="record-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={title.trim().length === 0}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="record-edit-save"
          >
            Save the draft
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
